import { computed, ref, watch, type Ref } from 'vue';

import { constant } from '@/constant';
import { ApiError } from '@/services/api.service';
import { formsService } from '@/services/forms.service';
import type { Form, MapStep } from '@/store/form.Interface';
import { useUserStore } from '@/store/userStore';
import { getMapMaxMarkers } from '@/utils/map-marker-cap';

/**
 * What the server did with a marker the participant just placed. The caller decides how to tell
 * them about it, so this composable stays free of toasts and analytics.
 */
export type MarkerSubmissionOutcome = 'published' | 'pending' | 'limit-reached' | 'failed';

type MarkerLike = { action?: number; user_id: unknown };

type PlacedMarker = {
  marker_location: object[];
  marker_text: string;
  marker_category?: number | null;
  marker_approved?: object;
};

/**
 * Owns the marker data a consultation map both reads and writes.
 *
 * The map component is a pure renderer: it takes points in and announces what the participant did,
 * but never persists anything itself. That leaves the host holding three jobs — merging the
 * server's markers with ones placed in this session, keeping like counts current after a vote, and
 * making the calls. This keeps all three together instead of spreading them through the host.
 */
export function useFormMapMarkers(formRef: Ref<Form>) {
  const userStore = useUserStore();

  /** Markers placed in this session, shown before (and only if) the server accepts them. */
  const placedMarkers = ref<any[]>([]);
  let placedMarkerSequence = 0;

  /** Like lists changed in this session, keyed by comment id, layered over the server's copy. */
  const markerLikeOverrides = ref<Record<number, any[]>>({});

  const mapMarkers = computed(() =>
    [...(formRef.value?.mapMarkers ?? []), ...placedMarkers.value].map((marker) =>
      marker.comment_id != null && markerLikeOverrides.value[marker.comment_id]
        ? { ...marker, likes: markerLikeOverrides.value[marker.comment_id] }
        : marker,
    ),
  );

  /**
   * The participant's markers as the API last counted them in this session: every vote of their response, read in the
   * vote's transaction, so it includes a marker placed from another tab. Null until a vote answers, and again once the
   * form reloads with a fresher response.
   */
  const markerCountFromApi = ref<number | null>(null);
  watch(
    () => formRef.value?.response,
    () => {
      markerCountFromApi.value = null;
    },
  );

  /**
   * The participant's markers on this form, as the API counts them against the map's cap
   * (formsServices.countUserVotes): every vote of their response, a marker held for moderation included.
   */
  const participantMarkerCount = computed(
    () => markerCountFromApi.value ?? formRef.value?.response?.[0]?.votes?.length ?? 0,
  );

  const mapMaxMarkers = () =>
    getMapMaxMarkers(formRef.value?.answers?.steps?.find((step): step is MapStep => step.type === 'map')?.map);

  async function submitMarker(marker: PlacedMarker): Promise<MarkerSubmissionOutcome> {
    // Show the marker straight away, then take it back if the server does not accept it.
    const clientKey = `placed-${++placedMarkerSequence}`;
    placedMarkers.value = [
      ...placedMarkers.value,
      {
        ...marker,
        clientKey,
        user: userStore.getUserName,
        user_id: userStore.getUserId,
        likes: [],
        form_id: formRef.value.id,
      },
    ];
    const discardPlacedMarker = () => {
      placedMarkers.value = placedMarkers.value.filter((placed) => placed.clientKey !== clientKey);
    };

    let response;
    try {
      response = await formsService.postMapVote(marker, formRef.value);
    } catch (error) {
      discardPlacedMarker();
      if (error instanceof ApiError && error.status === 429 && error.code === 'marker-limit') {
        // The page counted fewer, so the others came from elsewhere, such as another tab: the cap is reached all the
        // same, and the add button greys out.
        markerCountFromApi.value = mapMaxMarkers();
        return 'limit-reached';
      }
      console.error(error);
      return 'failed';
    }

    markerCountFromApi.value = response.data?.vote?.votes?.length ?? null;
    if (response.data?.moderationStatus === constant.FormComments.ModerationStatus.Pending) {
      // Held for moderation means it is not published, so it must not stay on the map.
      discardPlacedMarker();
      return 'pending';
    }
    return 'published';
  }

  /** Rejects when the server refuses the like or the unlike, for the caller to tell the participant. */
  function likeMarker(properties: Record<string, any>) {
    const userId = userStore.getUserId;
    const commentId = properties.markerCommentId;
    // A copy, so that a refused like leaves the list it came from as it was.
    const likes = [...parseLikes(properties.markerLike)];
    const existing = likes.findIndex((l) => l.user_id === userId);
    const previousLikes = markerLikeOverrides.value[commentId];

    if (existing === -1) {
      likes.push({ action: 1, user_id: userId });
    } else {
      likes.splice(existing, 1);
    }

    if (commentId != null) {
      markerLikeOverrides.value = { ...markerLikeOverrides.value, [commentId]: likes };
    }

    const action = existing === -1 ? 'like' : 'unlike';
    return formsService.postCommentAction(properties.markerFormId, commentId, action).catch((error) => {
      // Not saved: the pin goes back to the likes it showed before the click.
      if (commentId != null) {
        markerLikeOverrides.value = { ...markerLikeOverrides.value, [commentId]: previousLikes };
      }
      throw error;
    });
  }

  return { mapMarkers, participantMarkerCount, submitMarker, likeMarker };
}

/** Mapbox hands feature properties back as strings, and the list may be absent or malformed. */
function parseLikes(raw: unknown): MarkerLike[] {
  if (Array.isArray(raw)) return raw;
  try {
    return JSON.parse((raw as string) ?? '[]') ?? [];
  } catch {
    return [];
  }
}
